package com.appsyde.shared.presentation.screen.screen

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.rememberCoroutineScope
import com.appsyde.shared.modules.auth.domain.model.DomainResult
import com.appsyde.shared.modules.auth.domain.repo.AuthRepository
import com.appsyde.shared.modules.invite.data.source.AcceptInviteResult
import com.appsyde.shared.modules.invite.data.source.InviteAcceptDataSource
import com.appsyde.shared.modules.invite.data.source.InvitePreview
import com.appsyde.shared.presentation.style.animations.AppStylesAnimations
import com.appsyde.shared.presentation.style.style.AppStylesAccount
import com.appsyde.shared.presentation.style.style.AppStylesGlobal
import com.appsyde.shared.presentation.style.style.AppStylesHeader
import com.appsyde.shared.presentation.style.style.AppStylesLayout
import com.appsyde.shared.clearAccessToken
import com.appsyde.shared.setAccessToken
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.dom.*
import org.koin.core.context.GlobalContext
import com.appsyde.shared.getAccessToken
import com.appsyde.shared.presentation.style.style.AppStylesWizard

/**
 * Page d'acceptation d'invitation : alignée visuellement sur l'onboarding (header, fond, card 720px).
 * Flow en 2 étapes : 1) Se connecter avec Google → 2) Accepter l'invitation.
 */
@Composable
fun InviteAcceptPage() {
    Style(AppStylesAnimations)
    Style(AppStylesGlobal)
    Style(AppStylesHeader)
    Style(AppStylesLayout)
    Style(AppStylesWizard)
    Style(AppStylesAccount)

    val token = remember { getInviteTokenFromUrl() }
    val supportInboxFromUrl = getInboxFromUrl()
    val authRepo = remember { GlobalContext.get().get<AuthRepository>() }
    val acceptDataSource = remember { GlobalContext.get().get<InviteAcceptDataSource>() }
    val scope = rememberCoroutineScope()

    var inviteStep by remember { mutableStateOf<InviteStep>(InviteStep.Initial) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var inviteErrorFromRedirect by remember { mutableStateOf<String?>(null) }
    var isAccepting by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        val search = window.location.search
        getInviteErrorFromUrl()?.let { msg ->
            inviteErrorFromRedirect = msg
            inviteStep = InviteStep.NoToken
        }
    }

    LaunchedEffect(Unit) {
        val hash = window.location.hash
        val search = window.location.search
        if (hash.startsWith("#accessToken=")) {
            val encoded = hash.removePrefix("#accessToken=").trim()
            if (encoded.isNotBlank()) {
                val decoded = (window.asDynamic().decodeURIComponent as (String) -> String)(encoded)
                setAccessToken(decoded)
                window.history.replaceState(null, "", window.location.pathname + window.location.search)
                if (token != null) inviteStep = InviteStep.CanAccept
            }
        } else if (token != null && search.contains("auth=1")) {
            inviteStep = InviteStep.CanAccept
            val cleanSearch = search.replace(Regex("([?&])auth=1&?"), "$1").replace(Regex("&$|\\?$"), "").let { if (it == "?") "" else it }
            window.history.replaceState(null, "", window.location.pathname + cleanSearch)
        }
    }

    // CanAccept uniquement si on revient d'OAuth (#accessToken ou auth=1), pas via un token stocké.
    // Sinon on affiche d'abord NeedLogin (Se connecter), puis après OAuth on passera à CanAccept.
    LaunchedEffect(token) {
        if (inviteStep == InviteStep.CanAccept) return@LaunchedEffect
        if (token == null) inviteStep = InviteStep.NoToken
        else inviteStep = InviteStep.NeedLogin
    }

    // Données affichées : URL (inbox=) d'abord, sinon preview API quand on a le token (ex. après redirect OAuth sans inbox)
    var invitePreview by remember { mutableStateOf<InvitePreview?>(null) }
    LaunchedEffect(token) {
        if (token != null && supportInboxFromUrl.isNullOrBlank()) {
            invitePreview = acceptDataSource.getPreview(token)
        }
    }
    val supportInboxDisplay = supportInboxFromUrl?.trim()?.takeIf { it.isNotBlank() }
        ?: invitePreview?.supportInbox?.trim()?.takeIf { it.isNotBlank() }
        ?: "—"
    val inviterDisplayName = invitePreview?.inviterName?.takeIf { it.isNotBlank() } ?: "Un membre de l'équipe"

    Div(attrs = { classes(AppStylesGlobal.appRoot) }) {
        // Header invite : moins haut, brand gauche (AppSyde + Invitation), pas d’icône météo, droite discrète
        Div(attrs = { classes(AppStylesHeader.header) }) {
            Div(attrs = { classes(AppStylesAccount.invitePageHeaderInner) }) {
                Div(attrs = { classes(AppStylesHeader.brandRow) }) {
                    Img("/appsydeIcon.png", attrs = {
                        classes(AppStylesHeader.brandIcon)
                        attr("alt", "AppSyde")
                    })
                    Div(attrs = { classes(AppStylesHeader.brandCol) }) {
                        Div(attrs = { classes(AppStylesHeader.brand) }) { Text("AppSyde") }
                        Span(attrs = { classes(AppStylesHeader.brandSubtitle) }) { Text("Invitation") }
                    }
                }
                Span(attrs = { classes(AppStylesAccount.invitePageHeaderRight) }) { Text("Rejoindre une équipe") }
            }
        }

        Div(attrs = { classes(AppStylesGlobal.global, AppStylesLayout.globalContentWrap, AppStylesLayout.wizardPageWrap) }) {
            Div(attrs = { classes(AppStylesLayout.wizardContentWrapCentered) }) {
                Div(attrs = { classes(AppStylesAccount.inviteCardWrap) }) {
                    Div(attrs = { classes(AppStylesAccount.inviteCard) }) {
                        Div(attrs = { classes(AppStylesAccount.inviteCardInner, AppStylesAnimations.pageReveal) }) {
                            when (inviteStep) {
                                InviteStep.NoToken -> {
                                    Div(attrs = { classes(AppStylesAccount.inviteTitleRow) }) {
                                        Div(attrs = { classes(AppStylesAccount.inviteHeroBadge) }) {
                                            Span(attrs = { classes(AppStylesAccount.inviteHeroBadgeIcon) }) { Text("👥") }
                                        }
                                        Div(attrs = { classes(AppStylesAccount.inviteTitle) }) { Text("Lien invalide") }
                                    }
                                    Div(attrs = { classes(AppStylesAccount.inviteDescription) }) {
                                        Text(inviteErrorFromRedirect ?: "Ce lien d'invitation est invalide ou manquant.")
                                    }
                                    Div(attrs = { classes(AppStylesAccount.inviteActionWrap) }) {
                                        Button(attrs = {
                                            classes(AppStylesAccount.inviteBtnPrimary)
                                            attr("type", "button")
                                            onClick { window.location.href = "/" }
                                        }) { Text("Retour à l'accueil") }
                                    }
                                }
                                InviteStep.Success -> {
                                    Div(attrs = { classes(AppStylesAccount.inviteTitleRow) }) {
                                        Div(attrs = { classes(AppStylesAccount.inviteHeroBadge) }) {
                                            Span(attrs = { classes(AppStylesAccount.inviteHeroBadgeIcon) }) { Text("✔") }
                                        }
                                        Div(attrs = { classes(AppStylesAccount.inviteTitle) }) { Text("Invitation acceptée") }
                                    }
                                    Div(attrs = { classes(AppStylesAccount.inviteDescription) }) {
                                        Text("Vous faites partie de l'équipe. Redirection…")
                                    }
                                    LaunchedEffect(Unit) { window.location.href = "/" }
                                }
                                InviteStep.AlreadyUsed -> {
                                    Div(attrs = { classes(AppStylesAccount.inviteTitleRow) }) {
                                        Div(attrs = { classes(AppStylesAccount.inviteHeroBadge) }) {
                                            Span(attrs = { classes(AppStylesAccount.inviteHeroBadgeIcon) }) { Text("✔") }
                                        }
                                        Div(attrs = { classes(AppStylesAccount.inviteTitle) }) { Text("Invitation déjà acceptée") }
                                    }
                                    Div(attrs = { classes(AppStylesAccount.inviteDescription) }) {
                                        Text("Vous avez déjà rejoint l'équipe avec ce lien. Vous pouvez accéder à l'application.")
                                    }
                                    Div(attrs = { classes(AppStylesAccount.inviteActionWrap) }) {
                                        Button(attrs = {
                                            classes(AppStylesAccount.inviteBtnPrimary)
                                            attr("type", "button")
                                            onClick { window.location.href = "/" }
                                        }) { Text("Accéder à l'application") }
                                    }
                                }
                                InviteStep.NeedLogin, InviteStep.CanAccept -> {
                                    Div(attrs = { classes(AppStylesAccount.inviteTitleRow) }) {
                                        Div(attrs = { classes(AppStylesAccount.inviteHeroBadge) }) {
                                            Span(attrs = { classes(AppStylesAccount.inviteHeroBadgeIcon) }) { Text("👥") }
                                        }
                                        Div(attrs = { classes(AppStylesAccount.inviteTitle) }) {
                                            Text("Rejoindre l'équipe")
                                        }
                                    }
                                    Div(attrs = { classes(AppStylesAccount.inviteDescription) }) {
                                        Span(attrs = { classes(AppStylesAccount.inviteInviterName) }) { Text(inviterDisplayName) }
                                        Text(" vous a invité à accéder à cette boîte support dans AppSyde.")
                                    }
                                    Div(attrs = { classes(AppStylesAccount.inviteDescription) }) {
                                        Text("Connectez-vous avec le compte Google invité pour continuer.")
                                    }
                                    Div(attrs = { classes(AppStylesAccount.inviteSupportBlock) }) {
                                        Div(attrs = { classes(AppStylesAccount.inviteSupportLabel) }) { Text("BOÎTE SUPPORT") }
                                        Div(attrs = { classes(AppStylesAccount.inviteSupportValue) }) { Text(supportInboxDisplay) }
                                    }
                                    errorMessage?.let { msg ->
                                        Div(attrs = { classes(AppStylesAccount.inviteError) }) { Text(msg) }
                                    }
                                    Div(attrs = { classes(AppStylesAccount.inviteActionWrap) }) {
                                        when (inviteStep) {
                                            InviteStep.NeedLogin -> {
                                                Button(attrs = {
                                                    classes(AppStylesAccount.inviteBtnPrimary)
                                                    attr("type", "button")
                                                    onClick {
                                                        scope.launch {
                                                            val redirectUri = "${window.location.origin}${window.location.pathname}${window.location.search}"
                                                            when (val res = authRepo.getLoginUrlIdentity(redirectUri)) {
                                                                is DomainResult.Success ->
                                                                    window.location.href = res.value
                                                                is DomainResult.Failure ->
                                                                    { /* log or show error */ }
                                                            }
                                                        }
                                                        Unit
                                                    }
                                                }) { Text("Se connecter avec Google") }
                                                Span(attrs = { classes(AppStylesAccount.inviteActionSpacer) }) {}
                                                Button(attrs = {
                                                    classes(AppStylesAccount.inviteLinkSecondary)
                                                    attr("type", "button")
                                                    onClick { inviteStep = InviteStep.CanAccept }
                                                }) { Text("Déjà connecté ? Accepter l'invitation") }
                                            }
                                            InviteStep.CanAccept -> {
                                                Button(attrs = {
                                                    classes(AppStylesAccount.inviteBtnPrimary)
                                                    attr("type", "button")
                                                    onClick {
                                                        isAccepting = true
                                                        errorMessage = null
                                                        scope.launch {
                                                            val result = token?.let { acceptDataSource.acceptInvite(it) }
                                                            isAccepting = false
                                                            when (result) {
                                                                is AcceptInviteResult.Success -> {
                                                                    setAccessToken(result.accessToken)
                                                                    inviteStep = if (result.alreadyUsed) InviteStep.AlreadyUsed else InviteStep.Success
                                                                }
                                                                is AcceptInviteResult.NeedLogin -> {
                                                                    clearAccessToken()
                                                                    inviteStep = InviteStep.NeedLogin
                                                                }
                                                                is AcceptInviteResult.AlreadyUsed -> inviteStep = InviteStep.AlreadyUsed
                                                                is AcceptInviteResult.InvalidOrExpired -> {
                                                                    errorMessage = "Ce lien est invalide ou a expiré."
                                                                }
                                                                null -> inviteStep = InviteStep.NoToken
                                                            }
                                                        }
                                                        Unit
                                                    }
                                                }) {
                                                    Text(if (isAccepting) "Acceptation…" else "Accepter l'invitation")
                                                }
                                            }
                                            else -> {}
                                        }
                                    }
                                    Div(attrs = { classes(AppStylesAccount.inviteTrustRow) }) {
                                        Span(attrs = { classes(AppStylesAccount.inviteTrustCheck) }) { Text("✓") }
                                        Text("Session sécurisée. L'accès est accordé uniquement après validation.")
                                    }
                                }
                                else -> {}
                            }
                        }
                    }
                }
            }
        }
    }
}

private sealed class InviteStep {
    data object Initial : InviteStep()
    data object NeedLogin : InviteStep()
    data object CanAccept : InviteStep()
    data object NoToken : InviteStep()
    data object Success : InviteStep()
    data object AlreadyUsed : InviteStep()
}

/**
 * Lit le token d'invitation depuis l'URL (?token=...).
 * Ce token n'est jamais affiché ni injecté dans le DOM : il est uniquement envoyé au backend
 * dans POST /invites/accept. Le backend le valide (hash, expiration, usage unique).
 * Format attendu : base64url (caractères sûrs). On rejette le reste pour ne pas envoyer n'importe quoi.
 */
private fun getInviteTokenFromUrl(): String? {
    val search = window.location.search ?: return null
    if (!search.startsWith("?")) return null
    val raw = search.removePrefix("?").split("&").mapNotNull { param ->
        val parts = param.split("=", limit = 2)
        if (parts.getOrNull(0) == "token") parts.getOrNull(1) else null
    }.firstOrNull() ?: return null
    val decoded = try { js("decodeURIComponent").invoke(raw) as String } catch (_: Throwable) { raw }
    if (decoded.isBlank()) return null
    // base64url uniquement : évite d'envoyer du contenu arbitraire au backend
    if (!decoded.matches(Regex("^[A-Za-z0-9_-]+$"))) return null
    return decoded
}

/** Lit le message d'erreur depuis l'URL (?error=...) après redirection backend (invitation échouée). */
private fun getInviteErrorFromUrl(): String? {
    val search = window.location.search ?: return null
    if (!search.startsWith("?")) return null
    val raw = search.removePrefix("?").split("&").mapNotNull { param ->
        val parts = param.split("=", limit = 2)
        if (parts.getOrNull(0) == "error") parts.getOrNull(1) else null
    }.firstOrNull() ?: return null
    val code = try { (js("decodeURIComponent") as (String) -> String).invoke(raw) } catch (_: Throwable) { raw }
    return when (code.trim().lowercase()) {
        "invalid_token" -> "Ce lien d'invitation est invalide ou corrompu."
        "invite_invalid" -> "Ce lien a expiré ou n'existe plus."
        "email_mismatch" -> "L'adresse email utilisée ne correspond pas à celle de l'invitation."
        "user_not_found" -> "Impossible de finaliser l'invitation. Contactez l'équipe."
        else -> "Ce lien d'invitation est invalide ou manquant."
    }
}

/** Lit l'email de la boîte support depuis l'URL (?inbox=...), ajouté par le serveur dans le lien d'invitation. */
private fun getInboxFromUrl(): String? {
    fun parseQueryString(query: String): String? {
        if (query.isBlank()) return null
        val withoutQuestion = query.removePrefix("?").trim()
        if (withoutQuestion.isBlank()) return null
        val params = withoutQuestion.split("&")
        val inboxParam = params.mapNotNull { param ->
            val parts = param.split("=", limit = 2)
            if (parts.getOrNull(0)?.trim()?.lowercase() == "inbox") parts.getOrNull(1) else null
        }.firstOrNull() ?: return null
        val decoded = try {
            (js("decodeURIComponent") as (String) -> String).invoke(inboxParam)
        } catch (_: Throwable) {
            inboxParam.replace("%40", "@")
        }
        return decoded.trim().takeIf { it.isNotBlank() }
    }
    val search = window.location.search
    val fromSearch = search?.takeIf { it.startsWith("?") }?.let { parseQueryString(it) }
    if (!fromSearch.isNullOrBlank()) return fromSearch
    // Fallback: lire depuis l'URL complète (au cas où search serait vide en premier rendu)
    val href = window.location.href
    val questionIndex = href.indexOf('?')
    if (questionIndex >= 0 && questionIndex < href.length - 1) {
        val queryPart = href.substring(questionIndex)
        return parseQueryString(queryPart)
    }
    return null
}
